/**
 * Makes the Razorpay affordability (EMI) widget fill the product column.
 *
 * The widget renders inside an open shadow root, and its own styles hardcode
 * `main { width: 500px; max-width: 500px }` and `.container { width: 424px }`.
 * Page CSS cannot cross a shadow boundary and the app exposes no ::part() or
 * custom properties, so the only way to override it is to append a stylesheet
 * inside the shadow root itself.
 *
 * Everything here is a no-op when the widget is absent, so it costs nothing on
 * pages without it.
 */
const HOST = '#razorpay-affordability-widget main-component';
const MARK = 'data-rzp-fullwidth';

const CSS = `
  main, .container, .wrapper {
    width: 100% !important;
    max-width: 100% !important;
  }

  /* The card centres itself at its old fixed width; once it is fluid the auto
     margins would leave it off-centre in a wider column. */
  main, .container {
    margin-inline: 0 !important;
  }
`;

/** @param {ShadowRoot} root */
function applyTo(root) {
  if (root.querySelector(`style[${MARK}]`)) return false;

  const style = document.createElement('style');
  style.setAttribute(MARK, '');
  style.textContent = CSS;
  root.append(style);
  return true;
}

function sweep() {
  let applied = false;
  for (const host of document.querySelectorAll(HOST)) {
    // Closed roots are unreachable by design; skip rather than throw.
    if (host.shadowRoot) applied = applyTo(host.shadowRoot) || applied;
  }
  return applied;
}

/*
 * The app injects its widget well after DOMContentLoaded, and the theme
 * re-renders the product section on every variant change, which destroys and
 * rebuilds the block. A MutationObserver covers both without polling forever.
 */
const observer = new MutationObserver(() => sweep());

function start() {
  sweep();
  observer.observe(document.body, { childList: true, subtree: true });

  // Safety valve: stop observing the whole document once things have settled.
  // The widget is in place long before this, and leaving a subtree observer
  // running for the life of the page taxes every later DOM change.
  setTimeout(() => observer.disconnect(), 15000);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
